import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Bell, Moon, Eye, MessageSquare, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const defaultSettings = {
  showAppBanners: true,
  showSystemAnnouncements: true,
  toastAlerts: true,
  soundOnNotifications: false,
  badgeSidebar: true,
  doNotDisturb: false,
  visibleProfile: true,
  hideRolePrefs: false,
  hideContributions: false,
  allowMessages: true,
  showEmailToTeam: false,
};

const ControlPanel = ({ onClose }) => {
  const [settings, setSettings] = useState(defaultSettings);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${BACKEND_URL}/user/preferences`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();
        if (res.ok && data.preferences) {
          console.log("🧠 Loaded saved preferences:", data.preferences);
          setSettings(prev => ({ ...prev, ...data.preferences }));
        } else {
          console.warn("⚠️ No saved preferences found.");
        }
      } catch (err) {
        console.error("❌ Error loading preferences:", err);
      }
    };

    fetchSettings();
  }, []);

  const toggle = async (key, backendKey) => {
    const newValue = !settings[key];
    setSettings(prev => ({ ...prev, [key]: newValue }));

    console.log(`Setting changed — ${key}:`, newValue);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/settings/${backendKey}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ value: newValue }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      console.log(`✅ Synced ${backendKey}:`, newValue);
    } catch (err) {
      console.error(`❌ Failed to update ${backendKey}:`, err.message);
    }
  };

  return ReactDOM.createPortal(
    <div style={styles.overlay}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <h2>Control Panel</h2>
          <X onClick={onClose} style={styles.closeIcon} />
        </div>

        <Section icon={<Bell size={18} />} title="In-App Notifications">
          <ControlSwitch label="Enable pop-up notifications (toastify alerts)" value={settings.toastAlerts} onChange={() => toggle("toastAlerts", "toggle-toast-alerts")} />
        </Section>

        <Section icon={<Moon size={18} />} title="Do Not Disturb">
          <ControlSwitch label="Mute Email notifications" value={settings.doNotDisturb} onChange={() => toggle("doNotDisturb", "toggle-do-not-disturb")} />
        </Section>

        <Section icon={<Eye size={18} />} title="Profile Visibility">
          <ControlSwitch label="Hide my role preferences from public" value={settings.hideRolePrefs} onChange={() => toggle("hideRolePrefs", "toggle-hide-role-preferences")} />
          <ControlSwitch label="Hide my project contributions" value={settings.hideContributions} onChange={() => toggle("hideContributions", "toggle-hide-contributions")} />
        </Section>

        <Section icon={<MessageSquare size={18} />} title="Interaction Control">
          <ControlSwitch label="Allow others to message me" value={settings.allowMessages} onChange={() => toggle("allowMessages", "toggle-allow-messages")} />
        </Section>
      </div>
    </div>,
    document.getElementById("portal-root")
  );
};

const Section = ({ icon, title, children }) => (
  <div style={styles.section}>
    <h3 style={styles.sectionTitle}>
      {icon} <span style={{ marginLeft: 6 }}>{title}</span>
    </h3>
    {children}
  </div>
);

const ControlSwitch = ({ label, value, onChange }) => (
  <div style={styles.switchRow}>
    <span>{label}</span>
    <div
      onClick={onChange}
      style={{
        width: "48px",
        height: "24px",
        borderRadius: "999px",
        backgroundColor: value ? "#3B82F6" : "#ccc",
        display: "flex",
        alignItems: "center",
        padding: "2px",
        cursor: "pointer",
        transition: "background-color 0.3s ease-in-out",
      }}
    >
      <div
        style={{
          height: "20px",
          width: "20px",
          borderRadius: "50%",
          backgroundColor: "#fff",
          transform: value ? "translateX(24px)" : "translateX(0px)",
          transition: "transform 0.3s ease-in-out",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
        }}
      />
    </div>
  </div>
);

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10000,
  },
  panel: {
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    width: "500px",
    maxHeight: "80vh",
    overflowY: "auto",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  closeIcon: {
    cursor: "pointer",
    color: "#888",
  },
  section: {
    marginBottom: "24px",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "12px",
  },
  switchRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid #eee",
  },
};

export default ControlPanel;
